/** @jsxImportSource @emotion/react */
import React, { useCallback, useEffect, useMemo } from "react";
import { DisplayRankTitlesConstants, DisplayTitlesDisplayedConstants, DurationConstants, Model, QualifyFormatConstants, QualifyFormatOption, QualifyProductionCountryOption, QualifyProductionCountryPositionOption, ReportTypeConstants, RevenueEstimatorSourceModel } from "./Model";
import { Country, DayPart, DaysConstants, LocalGenre, OutputFormatConstants, OutputViewConstants, QualifyClassOneOption, QualifyClassTwoOption, QualifyDistributorOption, QualifyTypeOption, Station } from "model/Model";
import { CountryDescription } from "components/country-selector/CountryDescription";
import { CountrySelector } from "components/country-selector/CountrySelector";
import { StationsDescription } from "components/station-selector/StationsDescription";
import { StationSelector } from "components/station-selector/StationSelector";
import { ExpanderPanel } from "components/expander-panel/ExpanderPanel";
import { OutputDescription } from "components/output-selector/OutputDescription";
import { OutputSelector } from "components/output-selector/OutputSelector";
import { HelpText } from "components/help-text/HelpText";
import { DisplaySelector } from "./components/display-selector/DisplaySelector";
import { ReportTypeSelector } from "./components/report-type-selector/ReportTypeSelector";
import { Selectable, formatDateAsIso8601 } from "Utilities";
import { DateTimeSelector } from "./components/date-time-selector/DateTimeSelector";
import { DateTimeDescription } from "./components/date-time-selector/DateTimeDescription";
import { ReportTypeDescription } from "./components/report-type-selector/ReportTypeDescription";
import { QualifySelector } from "./components/qualify-selector/QualifySelector";
import { QualifyDescription } from "./components/qualify-selector/QualifyDescription";
import { DisplayDescription } from "./components/display-selector/DisplayDescription";
import { validateFromAndToDates, validateFromAndToTime, validateHaveDaypartInEachCountry, validateHaveDays, validateHaveStationInEachCountry, validateCountryStationsRevenueDates } from "ValidationHelpers";
import { ApplicationHeader } from "components/application-header/ApplicationHeader";
import { ChangeFromDate } from "reducers/fromDateReducers";
import { ChangeToDate } from "reducers/toDateReducer";
import { ChangeOutputFormat, ChangeOutputView } from "reducers/outputSlice";
import { ChangeFromTime } from "reducers/fromTimeReducer";
import { ChangeToTime } from "reducers/toTimeReducer";
import { ChangeUseDayParts } from "reducers/useDayPartsReducer";
import { ChangeDaysSelected } from "reducers/daysReducer";
import { ChangeDuration } from "./components/duration-selector/durationSlice";
import { ChangeReportType } from "./components/report-type-selector/reportTypeSlice";
import { ChangeQualifyClassOneIsSelected, ChangeQualifyClassTwoIsSelected, ChangeQualifyDistributorIsSelected, ChangeQualifyFormatIsSelected, ChangeQualifyProductionCountryIsSelected, ChangeQualifyProductionCountryPositionIsSelected, ChangeQualifyTypeIsSelected } from "./components/qualify-selector/qualifySlice";
import { ChangeDisplayRankTitles, ChangeDisplayTitlesDisplayed } from "./components/display-selector/displaySlice";
import { ChangeCountryIsSelected } from "reducers/countriesSlice";
import { ChangeStationIsSelected } from "reducers/stationsSlice";
import { ChangeDayPartIsSelected } from "reducers/dayPartReducer";
import { ChangeLocalGenreIsSelected } from "reducers/localGenreReducer";
import { useAppDispatch, useAppState } from "./app/hooks";
import { useApplicationContext } from "components/application-context/ApplicationContext";
import { ApplicationContextType } from "./Loader";
import { getSessionCredentials } from "contexts/UserContext";
import { ReportProgressMessageArgs, useNotifications } from "contexts/NotificationsContext";
import { useImmer } from "use-immer";
import { RunningState, downloadFile, emptyRunning } from "model/RunningState";
import { Runner } from "components/runner/Runner";
import { deleteJson, postJson, putJson } from "helpers/api";
import { useNavigate } from "react-router";
import { usePersonalization } from "contexts/PersonalizationContext";

const applicationUrl = "/Apps/RevenueEstimator";

type PanelState = {
  selectedPanel: string;
  country: boolean;
  origin: boolean;
  channel: boolean;
  dateTime: boolean;
  qualify: boolean;
  display: boolean;
  output: boolean;
};

const validate = ( applicationContext: ApplicationContextType, state: Model, showDateValidation: boolean ) => {
  const validation: {
    isValidForSubmit: boolean;
    warnings: Array<string>;
    errors: Array<string>;
    country?: string;
    reportType?: string;
    station?: string;
    dateTime?: string;
    qualify?: string;
    display?: string;
    output?: string;
  } = {
    isValidForSubmit: true,
    warnings: [],
    errors: []
  };

  if ( state.countries.length === 0 ) {
    validation.isValidForSubmit = false;
  }

  validateHaveStationInEachCountry( state.countries, state.stations, validation );

  validateFromAndToDates( state.dateTime.fromDate, state.dateTime.toDate, validation );

  validateHaveDays( state.dateTime.days, validation );

  if ( state.dateTime.useDayParts ) {
    validateHaveDaypartInEachCountry( state.countries, state.dateTime.dayParts, validation );
  } else {
    validateFromAndToTime( state.dateTime.fromTime, state.dateTime.toTime, validation );
  }

  if ( showDateValidation ) {
    const selectedCountries = applicationContext.countries.filter( m => state.countries.find( c => c === m.id ) );
    validateCountryStationsRevenueDates( selectedCountries, state.stations, state.dateTime.fromDate, state.dateTime.toDate, validation );
  }

  // NICE: qualify should require either 'all' or one of the options
  // NICE: validate display
  // NICE: validate output

  return validation;
};

const getDefaultPanelState = () => {
  const panelStateJson = localStorage.getItem( `ps:${ applicationUrl }` );
  if ( panelStateJson ) {
    return JSON.parse( panelStateJson ) as PanelState;
  } else {
    return { selectedPanel: "", country: false, origin: false, channel: false, dateTime: false, qualify: false, display: false, output: false };
  }
};

export const Index = (): JSX.Element => {

  const notifications = useNotifications();
  const state = useAppState();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const applicationContext = useApplicationContext<ApplicationContextType>();
  const personalization = usePersonalization();

  const { token, userId } = getSessionCredentials();

  const [ panels, setPanels ] = useImmer<PanelState>( getDefaultPanelState() );
  const [ running, setRunning ] = useImmer<RunningState>( emptyRunning );
  const allExpanded = panels.country && panels.origin && panels.channel && panels.dateTime && panels.qualify && panels.display && panels.output;

  useEffect( () => {
    // savePanelState
    localStorage.setItem( `ps:${ applicationUrl }`, JSON.stringify( panels ) );
  }, [ panels ] );

  const missingStation: Selectable<Station> = useMemo( () => ( { name: "MISSING", id: -1, isTranslated: false, isSelected: false, hasRevenue: false } ), [] );
  const missingDayPart: Selectable<DayPart> = useMemo( () => ( { time: "MISSING", name: "MISSING", id: -1, isSelected: false } ), [] );
  const missingLocalGenre: Selectable<LocalGenre> = useMemo( () => ( { name: "MISSING", id: -1, isSelected: false } ), [] );
  const missingType: QualifyTypeOption = useMemo( () => ( { value: "-1", name: "MISSING", isSelected: false } ), [] );
  const missingClassOne: QualifyClassOneOption = useMemo( () => ( { value: "-1", name: "MISSING", isSelected: false } ), [] );
  const missingClassTwo: QualifyClassTwoOption = useMemo( () => ( { value: "-1", name: "MISSING", isSelected: false } ), [] );
  const missingFormat: QualifyFormatOption = useMemo( () => ( { value: QualifyFormatConstants.Adaptation, name: "MISSING", isSelected: false } ), [] );
  const missingDistributor: QualifyDistributorOption = useMemo( () => ( { value: "-1", name: "MISSING", isSelected: false } ), [] );
  const missingProductionCountry: QualifyProductionCountryOption = useMemo( () => ( { value: "-1", name: "MISSING", isSelected: false } ), [] );
  const missingProductionCountryPosition: QualifyProductionCountryPositionOption = useMemo( () => ( { value: -1, name: "MISSING", isSelected: false } ), [] );

  const types: Array<Selectable<QualifyTypeOption>> = useMemo( () => applicationContext.etsTypes.map( m => ( { ...m, isSelected: !!state.qualify.selectedTypes.find( n => n === m.value ) } ) ), [ applicationContext.etsTypes, state.qualify.selectedTypes ] );
  const selectedTypes: Array<Selectable<QualifyTypeOption>> = useMemo( () => state.qualify.selectedTypes.map( m => ( { ...( applicationContext.etsTypes.find( n => n.value === m ) ?? missingType ), isSelected: true } ) ), [ applicationContext.etsTypes, missingType, state.qualify.selectedTypes ] );

  const classOnes: Array<Selectable<QualifyClassOneOption>> = useMemo( () => applicationContext.classOnes.map( m => ( { ...m, isSelected: !!state.qualify.selectedClassOnes.find( n => n === m.value ) } ) ), [ applicationContext.classOnes, state.qualify.selectedClassOnes ] );
  const selectedClassOnes: Array<Selectable<QualifyClassOneOption>> = useMemo( () => state.qualify.selectedClassOnes.map( m => ( { ...( applicationContext.classOnes.find( n => n.value === m ) ?? missingClassOne ), isSelected: true } ) ), [ applicationContext.classOnes, missingClassOne, state.qualify.selectedClassOnes ] );

  const classTwos: Array<Selectable<QualifyClassTwoOption>> = useMemo( () => applicationContext.classTwos.map( m => ( { ...m, isSelected: !!state.qualify.selectedClassTwos.find( n => n === m.value ) } ) ), [ applicationContext.classTwos, state.qualify.selectedClassTwos ] );
  const selectedClassTwos: Array<Selectable<QualifyClassTwoOption>> = useMemo( () => state.qualify.selectedClassTwos.map( m => ( { ...( applicationContext.classTwos.find( n => n.value === m ) ?? missingClassTwo ), isSelected: true } ) ), [ applicationContext.classTwos, missingClassTwo, state.qualify.selectedClassTwos ] );

  const formats: Array<Selectable<QualifyFormatOption>> = useMemo( () => applicationContext.formats.map( m => ( { ...m, isSelected: !!state.qualify.selectedFormats.find( n => n === m.value ) } ) ), [ applicationContext.formats, state.qualify.selectedFormats ] );
  const selectedFormats: Array<Selectable<QualifyFormatOption>> = useMemo( () => state.qualify.selectedFormats.map( m => ( { ...( applicationContext.formats.find( n => n.value === m ) ?? missingFormat ), isSelected: true } ) ), [ applicationContext.formats, missingFormat, state.qualify.selectedFormats ] );

  const distributors: Array<Selectable<QualifyDistributorOption>> = useMemo( () => applicationContext.distributors.map( m => ( { ...m, isSelected: !!state.qualify.selectedDistributors.find( n => n === m.value ) } ) ), [ applicationContext.distributors, state.qualify.selectedDistributors ] );
  const selectedDistributors: Array<Selectable<QualifyDistributorOption>> = useMemo( () => state.qualify.selectedDistributors.map( m => ( { ...( applicationContext.distributors.find( n => n.value === m ) ?? missingDistributor ), isSelected: true } ) ), [ applicationContext.distributors, missingDistributor, state.qualify.selectedDistributors ] );

  const productionCountries: Array<Selectable<QualifyProductionCountryOption>> = useMemo( () => applicationContext.productionCountries.map( m => ( { ...m, isSelected: !!state.qualify.selectedProductionCountries.find( n => n === m.value ) } ) ), [ applicationContext.productionCountries, state.qualify.selectedProductionCountries ] );
  const selectedProductionCountries: Array<Selectable<QualifyProductionCountryOption>> = useMemo( () => state.qualify.selectedProductionCountries.map( m => ( { ...( applicationContext.productionCountries.find( n => n.value === m ) ?? missingProductionCountry ), isSelected: true } ) ), [ applicationContext.productionCountries, missingProductionCountry, state.qualify.selectedProductionCountries ] );

  const productionCountryPositions: Array<Selectable<QualifyProductionCountryPositionOption>> = useMemo( () => applicationContext.productionCountryPositions.map( m => ( { ...m, isSelected: !!state.qualify.selectedProductionCountryPositions.find( n => n === m.value ) } ) ), [ applicationContext.productionCountryPositions, state.qualify.selectedProductionCountryPositions ] );
  const selectedProductionCountryPositions: Array<Selectable<QualifyProductionCountryPositionOption>> = useMemo( () => state.qualify.selectedProductionCountryPositions.map( m => ( { ...( applicationContext.productionCountryPositions.find( n => n.value === m ) ?? missingProductionCountryPosition ), isSelected: true } ) ), [ applicationContext.productionCountryPositions, missingProductionCountryPosition, state.qualify.selectedProductionCountryPositions ] );

  const countryGroups = useMemo( () => applicationContext.countryGroups.map( group => {
    const countries: Array<Selectable<Country>> = applicationContext.countries.filter( m => m.group === group ).map( m => ( { ...m, isSelected: !!state.countries.find( n => n === m.id ) } ) );
    const selectedCountryCount = countries.filter( m => m.isSelected ).length;

    return ( {
      name: group,
      countries: countries,
      isSelected: countries.length === selectedCountryCount
    } );
  } ), [ applicationContext.countries, applicationContext.countryGroups, state.countries ] );

  const countries = useMemo( () => applicationContext.countries.map( m => ( { ...m, isSelected: !!state.countries.find( c => c === m.id ) } ) ), [ applicationContext.countries, state.countries ] );

  const selectedCountries = useMemo( () => applicationContext.countries.filter( m => state.countries.find( c => c === m.id ) ).map( country => {
    const selectedStationIds = state.stations.find( c => c.id === country.id )?.stations ?? [];
    const selectedDayPartIds = state.dateTime.dayParts.find( c => c.id === country.id )?.dayParts ?? [];
    const selectedLocalGenreIds = state.qualify.selectedLocalGenres.find( c => c.id === country.id )?.localGenres ?? [];

    const stations: Array<Selectable<Station>> = country.stations.map( m => ( { ...m, isSelected: !!selectedStationIds.find( n => n === m.id ) } ) );
    const dayParts: Array<Selectable<DayPart>> = country.dayParts.map( m => ( { ...m, isSelected: !!selectedDayPartIds.find( n => n === m.id ) } ) );
    const localGenres: Array<Selectable<LocalGenre>> = ( country.localGenres ?? [] ).map( m => ( { ...m, isSelected: !!selectedLocalGenreIds.find( n => n === m.id ) } ) );

    const selectedStations: Array<Selectable<Station>> = selectedStationIds.map( m => stations.find( n => n.id === m ) ?? missingStation );
    const selectedDayParts: Array<Selectable<DayPart>> = selectedDayPartIds.map( m => dayParts.find( n => n.id === m ) ?? missingDayPart );
    const selectedLocalGenres: Array<Selectable<LocalGenre>> = selectedLocalGenreIds.map( m => localGenres.find( n => n.id === m ) ?? missingLocalGenre );

    return ( {
      ...country,
      stations,
      dayParts,
      localGenres,
      selectedStations,
      selectedDayParts,
      selectedLocalGenres,
      allStationsSelected: selectedStations.length === country.stations.length && country.stations.length !== 0,
      noStationsSelected: selectedStations.length === 0 && country.stations.length !== 0
    } );
  } ), [ applicationContext.countries, missingDayPart, missingLocalGenre, missingStation, state.countries, state.dateTime.dayParts, state.qualify.selectedLocalGenres, state.stations ] );

  const hasSelectedCountries = state.countries.length > 0;

  const isWorldSelected = useMemo( () => applicationContext.countries.length === state.countries.length, [ applicationContext.countries.length, state.countries.length ] );

  const qualify = useMemo( () => ( { ...state.qualify, types, selectedTypes, classOnes, selectedClassOnes, classTwos, selectedClassTwos, formats, selectedFormats, distributors, selectedDistributors, productionCountries, selectedProductionCountries, productionCountryPositions, selectedProductionCountryPositions } ), [ classOnes, classTwos, distributors, formats, productionCountries, productionCountryPositions, selectedClassOnes, selectedClassTwos, selectedDistributors, selectedFormats, selectedProductionCountries, selectedProductionCountryPositions, selectedTypes, state.qualify, types ] );

  const validation = useMemo( () => validate( applicationContext, state, personalization.dateWarnings ), [ applicationContext, personalization.dateWarnings, state ] );

  const onChangeCountryIsSelected = useCallback( ( countryId: number, isSelected: boolean ) => {
    const country = applicationContext.countriesById[ countryId ];
    dispatch( ChangeCountryIsSelected( { countryId, isSelected, clearExisting: false, defaultStations: country.defaultStations, defaultDemos: country.defaultDemos } ) );
  }, [ applicationContext.countriesById, dispatch ] );

  const onChangeStationIsSelected = useCallback( ( countryId: number, stationId: number, isSelected: boolean ) => dispatch( ChangeStationIsSelected( { countryId, stationId, isSelected } ) ), [ dispatch ] );

  const reportTypeEvents = useMemo( () => ( {
    onChangeReportType: ( reportType: ReportTypeConstants ) => { dispatch( ChangeReportType( reportType ) ); }
  } ), [ dispatch ] );

  const dateTimeEvents = useMemo( () => ( {
    onChangeFromDate: ( fromDate: number ) => dispatch( ChangeFromDate( fromDate ) ),
    onChangeToDate: ( fromDate: number ) => dispatch( ChangeToDate( fromDate ) ),
    onChangeDays: ( days: Array<DaysConstants> ) => { dispatch( ChangeDaysSelected( days ) ); },
    onChangeDuration: ( duration: DurationConstants ) => { dispatch( ChangeDuration( duration ) ); },
    onChangeFromTime: ( fromTime: string ) => { dispatch( ChangeFromTime( fromTime ) ); },
    onChangeToTime: ( toTime: string ) => { dispatch( ChangeToTime( toTime ) ); },
    onChangeUseDayParts: ( useDayParts: boolean ) => { dispatch( ChangeUseDayParts( useDayParts ) ); },
    onChangeDayPartIsSelected: ( countryId: number, dayPartId: number, isSelected: boolean ) => dispatch( ChangeDayPartIsSelected( { countryId, dayPartId, isSelected } ) )
  } ), [ dispatch ] );

  const qualifyEvents = useMemo( () => ( {
    onChangeQualifyTypeIsSelected: ( value: string, isSelected: boolean ) => dispatch( ChangeQualifyTypeIsSelected( { value, isSelected } ) ),
    onChangeQualifyClassOneIsSelected: ( value: string, isSelected: boolean ) => dispatch( ChangeQualifyClassOneIsSelected( { value, isSelected } ) ),
    onChangeQualifyClassTwoIsSelected: ( value: string, isSelected: boolean ) => dispatch( ChangeQualifyClassTwoIsSelected( { value, isSelected } ) ),
    onChangeQualifyFormatIsSelected: ( value: QualifyFormatConstants, isSelected: boolean ) => dispatch( ChangeQualifyFormatIsSelected( { value, isSelected } ) ),
    onChangeQualifyDistributorIsSelected: ( value: string, isSelected: boolean ) => dispatch( ChangeQualifyDistributorIsSelected( { value, isSelected } ) ),
    onChangeQualifyProductionCountryIsSelected: ( value: string, isSelected: boolean ) => dispatch( ChangeQualifyProductionCountryIsSelected( { value, isSelected } ) ),
    onChangeQualifyProductionCountryPositionIsSelected: ( value: number, isSelected: boolean ) => dispatch( ChangeQualifyProductionCountryPositionIsSelected( { value, isSelected } ) ),
    onChangeQualifyLocalGenreIsSelected: ( countryId: number, value: number, isSelected: boolean ) => dispatch( ChangeLocalGenreIsSelected( { countryId, localGenreId: value, isSelected } ) )
  } ), [ dispatch ] );

  const outputEvents = useMemo( () => ( {
    onChangeOutputFormat: ( value: OutputFormatConstants ) => dispatch( ChangeOutputFormat( value ) ),
    onChangeOutputView: ( value: OutputViewConstants ) => dispatch( ChangeOutputView( value ) )
  } ), [ dispatch ] );

  const displayEvents = useMemo( () => ( {
    onChangeDisplayRankTitles: ( value: DisplayRankTitlesConstants ) => { dispatch( ChangeDisplayRankTitles( value ) ); },
    onChangeDisplayTitlesDisplayed: ( value: DisplayTitlesDisplayedConstants ) => { dispatch( ChangeDisplayTitlesDisplayed( value ) ); }
  } ), [ dispatch ] );

  const getSourceModel = useCallback( (): RevenueEstimatorSourceModel => {
    const model: RevenueEstimatorSourceModel = {
      countries: state.countries,
      dateTime: {
        ...state.dateTime,
        fromDate: formatDateAsIso8601( state.dateTime.fromDate ),
        toDate: formatDateAsIso8601( state.dateTime.toDate )
      },
      qualify: state.qualify,
      reportType: state.reportType,
      display: state.display,
      output: state.output,
      stations: state.stations
    };

    return model;
  }, [ state.countries, state.dateTime, state.display, state.output, state.qualify, state.reportType, state.stations ] );

  const doSubmit = useCallback( async () => {
    if ( validation.errors.length > 0 ) {
      const message = validation.errors.join( "\n" );
      alert( message );
      return;
    }

    if ( validation.warnings.length > 0 ) {
      const message = validation.warnings.join( "\n" );
      if ( !globalThis.window.confirm( message ) ) return;
    }

    setRunning( emptyRunning );

    const model = getSourceModel();

    const result = await postJson<{ id: number; }>( applicationUrl, userId, token, model );
    if ( result ) setRunning( m => { m.id = result.id; } );

  }, [ getSourceModel, setRunning, token, userId, validation.errors, validation.warnings ] );

  const doCancel = useCallback( async () => {
    if ( !running.id ) return;

    await deleteJson( applicationUrl, userId, token, { id: running.id } );

    setRunning( m => { m.cancellationRequested = true; } );
  }, [ running.id, setRunning, token, userId ] );

  useEffect( () => {
    if ( applicationContext.templateOrHistoryId === 0 ) setPanels( m => { m.selectedPanel = ""; m.country = true; m.origin = false; m.channel = false; m.dateTime = false; m.qualify = false; m.display = false; m.output = false; } );
    setRunning( emptyRunning );
  }, [ applicationContext.templateOrHistoryId, setPanels, setRunning ] );

  const reportProgressMessageHandler = useCallback( ( args: ReportProgressMessageArgs ) => {
    console.log( args );

    if ( !args ) return;
    if ( args.id !== running.id ) return;

    setRunning( m => {
      m.percentComplete = args.percentComplete;
      m.message = args.message;
      m.url = args.url;
      m.contentType = args.contentType;
      m.status = args.status;
      if ( args.status !== "Started" ) {
        m.id = undefined;
      }
    } );
    if ( args.status === "Completed" && args.contentType !== "text/html" ) {
      // perform a download
      downloadFile( args.url, token );
      setRunning( emptyRunning );
      return;
    }
  }, [ running.id, setRunning, token ] );

  useEffect( () => {
    notifications.setOnReportProgressMessageHandler( () => reportProgressMessageHandler );
  }, [ notifications, reportProgressMessageHandler ] );

  const toggleExpandedPanels = useCallback( (): void => {
    setPanels( m => {
      const isAllExpanded = allExpanded;
      m.origin = !isAllExpanded;
      m.country = !isAllExpanded;
      m.channel = !isAllExpanded;
      m.dateTime = !isAllExpanded;
      m.qualify = !isAllExpanded;
      m.display = !isAllExpanded;
      m.output = !isAllExpanded;
    } );
  }, [ allExpanded, setPanels ] );

  const onSaveTemplate = useCallback( async ( templateName: string, sharedWith: Array<number> ) => {
    const payload = {
      templateName,
      sharedWith,
      model: getSourceModel()
    };

    // positive values are history rows so always create a new template from those
    if ( applicationContext.templateOrHistoryId >= 0 ) {
      const response = await postJson<{ itemId: number; }>( `/UserTemplate/${ applicationContext.applicationId }`, userId, token, payload );
      navigate( `/RevenueEstimator/${ response.itemId }` );
    }
    else {
      const updateExistingTemplate = applicationContext.templateName === templateName;
      if ( updateExistingTemplate ) {
        await putJson( `/UserTemplate/${ applicationContext.applicationId }/${ applicationContext.templateOrHistoryId }`, userId, token, payload );
      } else {
        const response = await postJson<{ itemId: number; }>( `/UserTemplate/${ applicationContext.applicationId }`, userId, token, payload );
        navigate( `/RevenueEstimator/${ response.itemId }` );
      }
    }
  }, [ applicationContext.applicationId, applicationContext.templateName, applicationContext.templateOrHistoryId, getSourceModel, navigate, token, userId ] );

  return ( <>
    <ApplicationHeader applicationId={ applicationContext.applicationId } isAllExpanded={ allExpanded } title={ applicationContext.name } isEnabled={ hasSelectedCountries } onToggleExpanded={ toggleExpandedPanels } />
    <ExpanderPanel title="Country:" isExpanded={ panels.country } onExpanded={ ( isExpanded ) => { setPanels( m => { m.country = isExpanded; } ); } } description={ <CountryDescription warningMessage={ validation.country } isWorldSelected={ isWorldSelected } countryGroups={ countryGroups } /> } selectedExpanderPanel={ panels.selectedPanel } onSelected={ ( title ) => setPanels( m => { m.selectedPanel = title; } ) }>
      <CountrySelector canSelectAllCountriesInGroup={ false } countryGroups={ countryGroups } countries={ countries } isWorldSelected={ isWorldSelected } onChangeCountryIsSelected={ onChangeCountryIsSelected } />
    </ExpanderPanel>
    <ExpanderPanel title="Report Type:" isExpanded={ panels.origin && hasSelectedCountries } onExpanded={ ( isExpanded ) => setPanels( m => { m.origin = isExpanded; } ) } isEnabled={ hasSelectedCountries } description={ <ReportTypeDescription warningMessage={ validation.reportType } reportType={ state.reportType } /> } selectedExpanderPanel={ panels.selectedPanel } onSelected={ ( title ) => setPanels( m => { m.selectedPanel = title; } ) }>
      <ReportTypeSelector helpId="TODO" reportType={ state.reportType } { ...reportTypeEvents } />
    </ExpanderPanel>
    <ExpanderPanel title="Channel:" isExpanded={ panels.channel && hasSelectedCountries } onExpanded={ ( isExpanded ) => setPanels( m => { m.channel = isExpanded; } ) } isEnabled={ hasSelectedCountries } description={ <StationsDescription warningMessage={ validation.station } selectedCountries={ selectedCountries } /> } selectedExpanderPanel={ panels.selectedPanel } onSelected={ ( title ) => setPanels( m => { m.selectedPanel = title; } ) }>
      <span css={ { fontWeight: "bold", marginLeft: "4px" } }>Channels <HelpText helpId="channel" /></span>
      <StationSelector canSelectAllCountriesInGroup={ true } selectedCountries={ selectedCountries } onChangeStationIsSelected={ onChangeStationIsSelected } />
    </ExpanderPanel>
    <ExpanderPanel title="Date/Time:" isExpanded={ panels.dateTime && hasSelectedCountries } onExpanded={ ( isExpanded ) => setPanels( m => { m.dateTime = isExpanded; } ) } isEnabled={ hasSelectedCountries } description={ <DateTimeDescription warningMessage={ validation.dateTime } { ...state.dateTime } selectedCountries={ selectedCountries } /> } selectedExpanderPanel={ panels.selectedPanel } onSelected={ ( title ) => setPanels( m => { m.selectedPanel = title; } ) }>
      <DateTimeSelector { ...state.dateTime } selectedCountries={ selectedCountries } { ...dateTimeEvents } />
    </ExpanderPanel>
    <ExpanderPanel title="Qualify:" isExpanded={ panels.qualify && hasSelectedCountries } onExpanded={ ( isExpanded ) => setPanels( m => { m.qualify = isExpanded; } ) } isEnabled={ hasSelectedCountries } description={ <QualifyDescription warningMessage={ validation.qualify } selectedCountries={ selectedCountries } { ...qualify } /> } selectedExpanderPanel={ panels.selectedPanel } onSelected={ ( title ) => setPanels( m => { m.selectedPanel = title; } ) }>
      <QualifySelector { ...qualify } selectedCountries={ selectedCountries } { ...qualifyEvents } />
    </ExpanderPanel>
    <ExpanderPanel title="Display:" isExpanded={ panels.display && hasSelectedCountries } onExpanded={ ( isExpanded ) => setPanels( m => { m.display = isExpanded; } ) } isEnabled={ hasSelectedCountries } description={ <DisplayDescription warningMessage={ validation.display } { ...state.display } /> } selectedExpanderPanel={ panels.selectedPanel } onSelected={ ( title ) => setPanels( m => { m.selectedPanel = title; } ) }>
      <DisplaySelector  { ...state.display } { ...displayEvents } />
    </ExpanderPanel>
    <ExpanderPanel title="Output:" isExpanded={ panels.output && hasSelectedCountries } onExpanded={ ( isExpanded ) => setPanels( m => { m.output = isExpanded; } ) } isEnabled={ hasSelectedCountries } description={ <OutputDescription warningMessage={ validation.output } { ...state.output } /> } selectedExpanderPanel={ panels.selectedPanel } onSelected={ ( title ) => setPanels( m => { m.selectedPanel = title; } ) }>
      <OutputSelector includeCsv={ false } includeXlsx={ false } { ...state.output } isTemplateOrHistoryOwner={ applicationContext.templateOrHistoryOwnerId === userId } defaultFormat={ OutputFormatConstants.Html } onSaveTemplate={ onSaveTemplate } { ...outputEvents } />
    </ExpanderPanel>
    <Runner running={ running } isValidForSubmit={ validation.isValidForSubmit } doSubmit={ doSubmit } doCancel={ doCancel } view={ state.output.view } applicationWindow="RevenueEstimator" />
  </> );
};